import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';

export default function GlobalSearch() {
  const [query, setQuery]           = useState('');
  const [results, setResults]       = useState(null);
  const [open, setOpen]             = useState(false);
  const [loading, setLoading]       = useState(false);
  const [selected, setSelected]     = useState(-1);  // keyboard nav index
  const [searchError, setSearchError] = useState(false);
  const debounceRef                 = useRef(null);
  const inputRef                    = useRef(null);
  const wrapperRef                  = useRef(null);
  const queryRef                    = useRef('');
  const navigate                    = useNavigate();

  // Close on outside click
  useEffect(() => {
    function handler(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Ctrl+K / Cmd+K to focus
  useEffect(() => {
    function handler(e) {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
      if (e.key === 'Escape') {
        setOpen(false);
        inputRef.current?.blur();
      }
    }
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  const search = useCallback(async (q) => {
    if (q.length < 2) { setResults(null); setOpen(false); return; }
    setLoading(true);
    queryRef.current = q;
    try {
      const { data } = await api.get('/search', { params: { q } });
      if (queryRef.current !== q) return;  // stale response — discard
      setResults(data);
      setOpen(true);
      setSelected(-1);
    } catch {
      if (queryRef.current === q) {
        setSearchError(true);
        setOpen(true);
      }
    }
    finally {
      if (queryRef.current === q) setLoading(false);
    }
  }, []);

  function handleChange(e) {
    const v = e.target.value;
    setQuery(v);
    setSearchError(false);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(v), 250);
  }

  function handleClear() {
    setQuery('');
    setResults(null);
    setOpen(false);
    inputRef.current?.focus();
  }

  // Flatten results for keyboard nav
  const allItems = results ? [
    ...results.quotations.map(r => ({ type:'quotation', ...r })),
    ...results.suppliers.map(r => ({ type:'supplier', ...r })),
    ...results.products.map(r => ({ type:'product', ...r })),
  ] : [];

  function navigateTo(item) {
    setOpen(false);
    setQuery('');
    setResults(null);
    if (item.type === 'quotation') {
      const path = item.order_type && item.order_type !== 'quotation' ? `/orders/${item.id}` : `/quotations/${item.id}`;
      navigate(path);
    } else if (item.type === 'supplier') {
      navigate('/suppliers');
    } else if (item.type === 'product') {
      navigate('/products');
    }
  }

  function handleKeyDown(e) {
    if (!open || allItems.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setSelected(s => Math.min(s + 1, allItems.length - 1)); }
    if (e.key === 'ArrowUp')   { e.preventDefault(); setSelected(s => Math.max(s - 1, 0)); }
    if (e.key === 'Enter' && selected >= 0) { e.preventDefault(); navigateTo(allItems[selected]); }
  }

  const hasResults = results && (results.quotations.length + results.suppliers.length + results.products.length) > 0;

  return (
    <div ref={wrapperRef} style={{ position: 'relative', width: '100%', maxWidth: 400 }}>
      <div style={{ position: 'relative' }}>
        <span style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', fontSize:14, color:'var(--gray-400)', pointerEvents:'none' }}>🔍</span>
        <input
          ref={inputRef}
          value={query}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={() => results && setOpen(true)}
          placeholder="Buscar cotações, fornecedores, produtos… (Ctrl+K)"
          style={{
            width: '100%',
            paddingLeft: 32,
            paddingRight: query ? 28 : 8,
            height: 36,
            fontSize: 13,
            background: 'rgba(255,255,255,.12)',
            border: '1px solid rgba(255,255,255,.15)',
            borderRadius: 'var(--radius)',
            color: 'var(--gray-700)',
            outline: 'none',
          }}
        />
        {query && (
          <button onClick={handleClear} style={{ position:'absolute', right:6, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', color:'var(--gray-400)', fontSize:16, lineHeight:1 }}>✕</button>
        )}
      </div>

      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 500,
          background: 'white', border: '1px solid var(--gray-200)',
          borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-lg)',
          maxHeight: 380, overflowY: 'auto',
        }}>
          {searchError && (
            <div style={{ padding:'12px 16px', fontSize:13, color:'#e53e3e' }}>Erro ao buscar. Tente novamente.</div>
          )}

          {loading && (
            <div style={{ padding:'12px 16px', fontSize:13, color:'var(--gray-400)' }}>Buscando...</div>
          )}

          {!loading && !hasResults && query.length >= 2 && (
            <div style={{ padding:'20px 16px', textAlign:'center', fontSize:13, color:'var(--gray-400)' }}>
              Nenhum resultado para "<strong>{query}</strong>"
            </div>
          )}

          {!loading && hasResults && (() => {
            let globalIdx = 0;
            return (
              <>
                {results.quotations.length > 0 && (
                  <div>
                    <div style={{ padding:'8px 16px 4px', fontSize:11, fontWeight:700, color:'var(--gray-400)', textTransform:'uppercase', letterSpacing:.5 }}>Cotações / Pedidos</div>
                    {results.quotations.map(r => {
                      const idx = globalIdx++;
                      const isOrder = r.order_type && r.order_type !== 'quotation';
                      return (
                        <div key={r.id} onMouseDown={() => navigateTo({ type:'quotation', ...r })}
                          style={{ padding:'8px 16px', cursor:'pointer', background: selected===idx ? 'var(--gray-50)' : 'white', display:'flex', gap:10, alignItems:'center' }}>
                          <span style={{ fontSize:13 }}>{isOrder ? '📦' : '📋'}</span>
                          <div>
                            <div style={{ fontSize:13, fontWeight:600, fontFamily:'var(--font-mono)', color:'var(--pantone)' }}>{r.reference}</div>
                            <div style={{ fontSize:11, color:'var(--gray-500)' }}>{r.supplier_name}{r.collection ? ` · ${r.collection}` : ''}</div>
                          </div>
                          <span style={{ marginLeft:'auto', fontSize:11, padding:'2px 8px', borderRadius:10, background:'var(--gray-100)', color:'var(--gray-600)' }}>{r.status}</span>
                        </div>
                      );
                    })}
                  </div>
                )}

                {results.suppliers.length > 0 && (
                  <div>
                    <div style={{ padding:'8px 16px 4px', fontSize:11, fontWeight:700, color:'var(--gray-400)', textTransform:'uppercase', letterSpacing:.5 }}>Fornecedores</div>
                    {results.suppliers.map(r => {
                      const idx = globalIdx++;
                      return (
                        <div key={r.id} onMouseDown={() => navigateTo({ type:'supplier', ...r })}
                          style={{ padding:'8px 16px', cursor:'pointer', background: selected===idx ? 'var(--gray-50)' : 'white', display:'flex', gap:10, alignItems:'center' }}>
                          <span style={{ fontSize:13 }}>🏭</span>
                          <div>
                            <div style={{ fontSize:13, fontWeight:600 }}>{r.name}</div>
                            <div style={{ fontSize:11, color:'var(--gray-500)' }}>{[r.country, r.city, r.category].filter(Boolean).join(' · ')}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {results.products.length > 0 && (
                  <div>
                    <div style={{ padding:'8px 16px 4px', fontSize:11, fontWeight:700, color:'var(--gray-400)', textTransform:'uppercase', letterSpacing:.5 }}>Produtos</div>
                    {results.products.map(r => {
                      const idx = globalIdx++;
                      return (
                        <div key={r.id} onMouseDown={() => navigateTo({ type:'product', ...r })}
                          style={{ padding:'8px 16px', cursor:'pointer', background: selected===idx ? 'var(--gray-50)' : 'white', display:'flex', gap:10, alignItems:'center' }}>
                          <span style={{ fontSize:13 }}>📦</span>
                          <div>
                            <div style={{ fontSize:13, fontWeight:600 }}>{r.name}</div>
                            {r.category && <div style={{ fontSize:11, color:'var(--gray-500)' }}>{r.category}</div>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            );
          })()}
        </div>
      )}
    </div>
  );
}
